import { useMutation } from '@tanstack/react-query';

import { toast } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

interface DownloadVendorFormParams {
	orderId: string;
	formKey: string;
	formName?: string;
}

/**
 * API function to download a specific vendor form (POA form)
 */
export const downloadVendorForm = async (
	servicesProviderId: string,
	params: DownloadVendorFormParams,
): Promise<void> => {
	const { orderId, formKey, formName } = params;
	const filename = formName ? `${formName}.pdf` : `${formKey}.pdf`;
	
	await apiClient.downloadFile(
		ordersEndpoints.downloadVendorForm(servicesProviderId, orderId, formKey),
		undefined,
		'application/pdf',
		filename,
	);
};

/**
 * Hook for downloading vendor forms (POA forms)
 */
export const useDownloadVendorForm = () => {
	const servicesProviderId = useCurrentServicesProviderId();

	return useMutation({
		mutationFn: async (params: DownloadVendorFormParams) => {
			if (!params.orderId) {
				throw new Error('Unable to download vendor form without an order ID.');
			}
			if (!servicesProviderId) {
				throw new Error('Unable to download vendor form without a services provider ID.');
			}
			return downloadVendorForm(servicesProviderId, params);
		},
		onSuccess: () => {
			toast({
				message: 'Form downloaded successfully',
				variant: 'success',
			});
		},
		onError: (error) => {
			const errorMessage =
				error instanceof Error ? error.message : 'Failed to download form';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		},
	});
};

